'use client';

// components/css-carousel/CarouselControls.tsx
import React from 'react';
import { cn } from '@/lib/cn';
import { SpriteIcon } from '@/components/ui/IconSprite';

interface CarouselControlsProps {
  carouselId: string;
  className?: string;
  prevClassName?: string;
  nextClassName?: string;
  showPrev?: boolean;
  showNext?: boolean;
}

interface ControlButtonProps {
  type: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
  className?: string | undefined;
}

export function ControlButton({ type, disabled, onClick, className }: ControlButtonProps) {
  const chevronName = type === 'prev' ? 'chevron-left' : 'chevron-right';

  return (
    <button
      type="button"
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      data-analytics-skip="true"
      aria-label={type === 'prev' ? 'Previous slide' : 'Next slide'}
      className={cn(
        'flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',

        // State-based styling using semantic design tokens
        disabled
          ? 'border-tertiary bg-secondary cursor-not-allowed text-tertiary'
          : 'border-secondary bg-primary text-primary cursor-pointer hover:bg-primary-hover hover:border-secondary active:scale-95 shadow',
        className,
      )}
    >
      <SpriteIcon name={chevronName} className="h-3 w-3" />
    </button>
  );
}

export function CarouselControls({
  carouselId,
  className,
  prevClassName,
  nextClassName,
  showPrev = true,
  showNext = true,
}: CarouselControlsProps) {
  const [canGoPrev, setCanGoPrev] = React.useState(false);
  const [canGoNext, setCanGoNext] = React.useState(true);

  React.useEffect(() => {
    const readFromDataset = () => {
      const carousel = document.getElementById(carouselId);
      if (!carousel) return;
      setCanGoPrev(carousel.dataset.canGoPrev === 'true');
      setCanGoNext(carousel.dataset.canGoNext !== 'false');
    };

    // Initial sync (in case the first `carousel-update` already fired).
    readFromDataset();

    // Listen for carousel scroll events
    const handleCarouselUpdate = (event: CustomEvent) => {
      if (event.detail.carouselId === carouselId) {
        setCanGoPrev(!!event.detail.canGoPrev);
        setCanGoNext(!!event.detail.canGoNext);
      }
    };

    document.addEventListener('carousel-update', handleCarouselUpdate as EventListener);
    window.addEventListener('resize', readFromDataset);

    return () => {
      document.removeEventListener('carousel-update', handleCarouselUpdate as EventListener);
      window.removeEventListener('resize', readFromDataset);
    };
  }, [carouselId]);

  const goToPrev = () => {
    const event = new CustomEvent('carousel-control', {
      detail: { carouselId, action: 'prev' },
    });
    document.dispatchEvent(event);
  };

  const goToNext = () => {
    const event = new CustomEvent('carousel-control', {
      detail: { carouselId, action: 'next' },
    });
    document.dispatchEvent(event);
  };

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {showPrev && (
        <ControlButton
          type="prev"
          disabled={!canGoPrev}
          onClick={goToPrev}
          className={prevClassName}
        />
      )}

      {showNext && (
        <ControlButton
          type="next"
          disabled={!canGoNext}
          onClick={goToNext}
          className={nextClassName}
        />
      )}
    </div>
  );
}
